import { createContext, Fragment, useCallback, useContext, useLayoutEffect, useState, type ReactNode } from "react";
import { hasOwn } from "../lib/hasOwn";

/**
 * GOOGLE'S reCAPTCHA DISCLOSURE, on book.* and app.* (counsel and Founder,
 * 2026-10-09).
 *
 * App Check runs reCAPTCHA on every page of both surfaces, and Firebase
 * renders reCAPTCHA's badge inside its own `display: none` container, so the
 * badge is never visible (privacy inventory, 2026-10-09). Google allows
 * hiding the badge only when this line is shown in the user flow.
 *
 * ONE PER SCREEN (Design's spec, approved by Product, 2026-10-12): on a screen
 * with a protected action (Send code, Confirm booking, Sign in, Create account;
 * App's Sign in) it sits at that action and the footer copy is OMITTED from
 * the DOM; every other screen shows it as a footer row. The long sentence sits
 * directly above its action, two lines, broken after "reCAPTCHA", in both
 * languages. Book's Review and Sign in carry the SHORT notice below the
 * button instead (counsel, 2026-10-24; RECAPTCHA_NOTICE_SHORT below).
 *
 * THE COPY IS APPROVED VERBATIM, one entry per language, and lives only here:
 * both apps render this component, never a copy of the sentence. Each pair of
 * brackets is a link, in order: Google's Privacy Policy, then its Terms of
 * Service (`?hl=es` on Spanish).
 */
export const RECAPTCHA_NOTICE = {
  en: {
    text: "This site is protected by reCAPTCHA and the Google [Privacy Policy] and [Terms of Service] apply.",
    privacy: "https://policies.google.com/privacy",
    terms: "https://policies.google.com/terms",
    newTab: " (opens in a new tab)",
  },
  es: {
    text: "Este sitio está protegido por reCAPTCHA y se aplican la [Política de privacidad] y las [Condiciones del servicio] de Google.",
    privacy: "https://policies.google.com/privacy?hl=es",
    terms: "https://policies.google.com/terms?hl=es",
    newTab: " (se abre en una pestaña nueva)",
  },
} as const;

export type RecaptchaNoticeLocale = keyof typeof RECAPTCHA_NOTICE;

/**
 * The line, with both links. Each opens in a new tab (the reader keeps their
 * booking or form) and says so to screen readers, like the footer's Privacy
 * and Terms; `rel="noopener noreferrer"` and `referrerPolicy="no-referrer"`
 * (App's footer pattern) give Google no handle on this tab and no Referer
 * naming the page, an action link's code included.
 */
export function RecaptchaNotice({
  locale,
  testId = "recaptcha-notice",
  className,
  inBar = false,
  inFooter = false,
}: {
  locale: string;
  testId?: string;
  className?: string;
  /**
   * In an action area, directly above the button: left-aligned, 10px before
   * it. At large text or on short screens the action area flows with the
   * page and this copy stays in it. Use ActionRecaptchaNotice, which also
   * omits the footer copy.
   */
  inBar?: boolean;
  /**
   * The footer row, centred between the legal row and the company line. Use
   * FooterRecaptchaNotice, which renders only while no action copy is mounted.
   */
  inFooter?: boolean;
}) {
  const copy = hasOwn(RECAPTCHA_NOTICE, locale) ? RECAPTCHA_NOTICE[locale as RecaptchaNoticeLocale] : RECAPTCHA_NOTICE.en;
  const parts = copy.text.split(/\[([^\]]+)\]/);
  const BREAK_AFTER = "reCAPTCHA ";
  const hrefs = [copy.privacy, copy.terms];
  const ids = ["privacy", "terms"];
  return (
    <p className={["l-recaptcha-notice", inBar ? "is-in-bar" : null, inFooter ? "is-in-footer" : null, className].filter(Boolean).join(" ")} data-testid={testId}>
      {parts.map((part, index) => {
        // Odd indexes are the bracketed link texts, in order.
        if (index % 2 === 0) {
          // Two lines, broken after "reCAPTCHA" (Design). The space is kept
          // before the <br>, so the text reads "reCAPTCHA and…" when copied or
          // read aloud, and counsel's sentence is unchanged.
          const at = part.indexOf(BREAK_AFTER);
          if (at === -1) return <Fragment key={index}>{part}</Fragment>;
          return (
            <Fragment key={index}>
              {part.slice(0, at + BREAK_AFTER.length)}
              <br />
              {part.slice(at + BREAK_AFTER.length)}
            </Fragment>
          );
        }
        const link = (index - 1) / 2;
        return (
          <a key={index} className="l-recaptcha-link" href={hrefs[link]} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" data-testid={`${testId}-${ids[link]}`}>
            {part}
            <span className="l-visually-hidden">{copy.newTab}</span>
          </a>
        );
      })}
    </p>
  );
}

/*
 * RENDER-TIME OMISSION OF THE FOOTER COPY (Web Director, 2026-10-12). An
 * action-bar notice CLAIMS the footer while it is mounted; footers render
 * their copy only while nothing claims it. So each screen has exactly one
 * copy in the DOM, and conditional screens follow by themselves: Review's
 * uncertain state, Create account's non-form phases and /login's signed-in
 * state render no action notice, so the footer copy returns. The claim runs
 * in a layout effect, which commits before paint: no visible flash.
 */
const ClaimContext = createContext<(() => () => void) | null>(null);
const ClaimedContext = createContext(false);

export function ActionNoticeProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState(0);
  // Kept stable on purpose: it is ActionRecaptchaNotice's effect dependency,
  // so a new identity would release and re-claim on every provider render.
  const claim = useCallback(() => {
    setClaims((count) => count + 1);
    return () => setClaims((count) => count - 1);
  }, []);
  return (
    <ClaimContext.Provider value={claim}>
      <ClaimedContext.Provider value={claims > 0}>{children}</ClaimedContext.Provider>
    </ClaimContext.Provider>
  );
}

/**
 * THE SHORT NOTICE (counsel, 2026-10-24: "2026-10-24 - Booking Flow Legal
 * Text — Minimum Wording and Placement", section 3). Google's FAQ now allows
 * the hidden badge "as long as you include the reCAPTCHA branding visibly in
 * the user flow" with this text, and Lura's Privacy Policy and Cookie Notice
 * already name reCAPTCHA and link Google's policies. BELOW the final button on
 * Book's Review and on Book's Sign in only, no links; it is not a consent
 * sentence, so no consent version changes. Everywhere else (the footer, the
 * code step, Create account, App) keeps the long sentence above.
 */
export const RECAPTCHA_NOTICE_SHORT = {
  en: "This site is protected by reCAPTCHA.",
  es: "Este sitio está protegido por reCAPTCHA.",
} as const;

/**
 * The notice at a protected action; claims the footer while mounted. The
 * long sentence goes directly above the action; `short`, directly below it.
 */
// DELIBERATELY LEFT UNCOMPILED (Web Director, 2026-10-24). The default
// parameter makes the React Compiler skip this component on Babel 8. It is a
// stateless leaf, so compiling it buys nothing, and compiled it costs 51 B
// gzip in Book's entry, more than the payments build has left. The Babel 7.29
// ratchet (after v1.1.0) makes such opt-outs explicit.
export function ActionRecaptchaNotice({ locale, testId, short = false }: { locale: string; testId: string; short?: boolean }) {
  const claim = useContext(ClaimContext);
  useLayoutEffect(() => (claim ? claim() : undefined), [claim]);
  return short ? (
    <p className="l-recaptcha-notice is-in-bar is-short" data-testid={testId}>
      {locale === "es" ? RECAPTCHA_NOTICE_SHORT.es : RECAPTCHA_NOTICE_SHORT.en}
    </p>
  ) : (
    <RecaptchaNotice locale={locale} testId={testId} inBar />
  );
}

/** The footer row, rendered only while no action notice claims it. */
export function FooterRecaptchaNotice({ locale, testId = "footer-recaptcha" }: { locale: string; testId?: string }) {
  if (useContext(ClaimedContext)) return null;
  return <RecaptchaNotice locale={locale} testId={testId} inFooter />;
}
